/* MASONTEX · unified B2B inquiry controller
   Single owner for:
   - four-step inquiry wizard and entry triggers
   - buyer-information early capture + Inquiry ID continuity
   - helmet selection, configuration sync and editable review state
   - attribution retention and PII-safe funnel events
   - Shopify native contact-form submit lifecycle / hCaptcha
   - server-confirmed success / error states

   Product capability mapping remains isolated in ms-unified-inquiry-capabilities.js.
   Shopify's native contact form remains the only submit backend. */
(() => {
  'use strict';
  if (window.__MS_UNIFIED_INQUIRY_CONTROLLER__) return;
  window.__MS_UNIFIED_INQUIRY_CONTROLLER__ = true;

  const ATTRIBUTION_KEY = 'masontexInquiryAttribution';
  const SUBMIT_SESSION_KEY = 'masontexLastInquirySubmit';
  const JOURNEY_DRAFT_KEY = 'masontexInquiryJourneyDraft';
  const PENDING_TIMEOUT_MS = 20000;
  const CHECK_INTERVAL_MS = 1000;
  const INQUIRY_LINK_FRAGMENT = '/pages/wholesale-price-request';
  const BUYER_STAGE = 'Buyer Information Captured';
  const CONFIG_STAGE = 'Configuration Follow-up';
  const galleryConfigurations = new Map();
  const formLifecycles = new WeakMap();
  const dialogLifecycles = new WeakMap();
  const createLifecycle = () => {
    const cleanups = [];
    const timers = new Set();
    let active = true;
    return {
      add: (cleanup) => cleanups.push(cleanup),
      on(target, type, callback, options) {
        if (!target) return;
        target.addEventListener(type, callback, options);
        cleanups.push(() => target.removeEventListener(type, callback, options));
      },
      later(callback, delay) {
        const timer = window.setTimeout(() => {
          timers.delete(timer);
          if (active) callback();
        }, delay);
        timers.add(timer);
      },
      destroy() {
        if (!active) return;
        active = false;
        timers.forEach((timer) => window.clearTimeout(timer));
        timers.clear();
        cleanups.reverse().forEach((cleanup) => cleanup());
      }
    };
  };

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];
  const clean = (value = '') => String(value || '').trim();
  const productPath = (value) => {
    try { return new URL(value, window.location.href).pathname.replace(/\/$/, ''); }
    catch (_) { return ''; }
  };
  const readJson = (value, fallback = {}) => {
    try { return JSON.parse(value || ''); } catch (_) { return fallback; }
  };
  const sameObject = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});
  const readI18n = (root) => readJson(q(root, '[data-ms-unified-i18n]')?.textContent, {});

  // Storage can be blocked independently of the native inquiry form.
  const readStorage = (storageName, key) => {
    try { return window[storageName].getItem(key) || ''; } catch (_) { return ''; }
  };
  const readDraft = () => readJson(readStorage('sessionStorage', JOURNEY_DRAFT_KEY), {});
  const writeDraft = (draft) => {
    try { window.sessionStorage.setItem(JOURNEY_DRAFT_KEY, JSON.stringify(draft || {})); } catch (_) {}
  };
  const clearDraft = () => {
    try { window.sessionStorage.removeItem(JOURNEY_DRAFT_KEY); } catch (_) {}
  };

  const syncGalleryToForm = (form) => {
    const url = q(form, '[data-ms-unified-url]')?.value;
    const gallery = url ? galleryConfigurations.get(productPath(url)) : null;
    if (!gallery || form.closest('dialog:not([data-ms-single-form])')) return;
    const values = {
      'Helmet color': gallery.color || '',
      'Wholesale MOQ': gallery.moq ?? '',
      'Configuration summary': ['model', 'color', 'visor', 'structure', 'view']
        .map(key => gallery[key] ? `${key}: ${gallery[key]}` : '').filter(Boolean).join(' · '),
      'OEM configuration JSON': JSON.stringify({ productGallery: gallery })
    };
    for (const [name, value] of Object.entries(values)) {
      const field = form.elements[`contact[${name}]`];
      if (field) field.value = String(value);
    }
  };

  const rememberGalleryConfiguration = (event) => {
    const details = event.detail;
    const path = productPath(details?.productUrl);
    if (!path || !details || typeof details !== 'object') return;
    galleryConfigurations.set(path, details);
    qa(document, '[data-ms-unified-inquiry] form').forEach((form) => {
      if (productPath(q(form, '[data-ms-unified-url]')?.value) === path) syncGalleryToForm(form);
    });
  };
  document.addEventListener('oem-lite:ready', rememberGalleryConfiguration);
  document.addEventListener('oem-lite:change', rememberGalleryConfiguration);

  const makeInquiryId = () => {
    const now = new Date();
    const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    let token = '';
    try {
      const bytes = new Uint8Array(4);
      window.crypto.getRandomValues(bytes);
      token = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('').toUpperCase();
    } catch (_) {
      token = Math.random().toString(36).slice(2, 10).toUpperCase();
    }
    return `MS-${date}-${token}`;
  };

  const dispatch = (name, detail = {}) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
      document.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (_) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail));
    } catch (_) {}
  };

  const fireLeadEvents = (detail) => {
    try { if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', detail); } catch (_) {}
    try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', detail); } catch (_) {}
    try { if (window.ttq && typeof window.ttq.track === 'function') window.ttq.track('SubmitForm', detail); } catch (_) {}
  };

  const readAttribution = () => {
    const params = new URLSearchParams(window.location.search);
    const stored = readJson(readStorage('localStorage', ATTRIBUTION_KEY), {});
    const first = (...keys) => {
      for (const key of keys) {
        const value = clean(params.get(key));
        if (value) return value;
      }
      return '';
    };
    const landingPage = (() => {
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        return url.toString();
      } catch (_) { return window.location.href; }
    })();
    const data = {
      utm_source: first('utm_source') || stored.utm_source || '',
      utm_medium: first('utm_medium') || stored.utm_medium || '',
      utm_campaign: first('utm_campaign') || stored.utm_campaign || '',
      utm_content: first('utm_content') || stored.utm_content || '',
      utm_term: first('utm_term') || stored.utm_term || '',
      click_id: first('fbclid', 'gclid', 'ttclid', 'msclkid') || stored.click_id || '',
      landing_page: landingPage,
      language: document.documentElement.lang || navigator.language || ''
    };
    try { window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data)); } catch (_) {}
    return data;
  };

  const applyAttribution = (root, data) => {
    qa(root, '[data-ms-attr]').forEach((input) => {
      const value = data[input.dataset.msAttr] || '';
      if (input.value !== value) input.value = value;
    });
  };

  const applyUrlPrefill = (root) => {
    const params = new URLSearchParams(window.location.search);
    const handle = clean(params.get('product'));
    const model = clean(params.get('model'));
    if (!handle && !model) return;
    const handleInput = q(root, '[data-ms-unified-handle]');
    const modelInput = q(root, '[data-ms-unified-model]');
    const urlInput = q(root, '[data-ms-unified-url]');
    const productInput = q(root, '[data-ms-unified-product]');
    if (handle && handleInput && !clean(handleInput.value)) handleInput.value = handle;
    if (model && modelInput && !clean(modelInput.value)) modelInput.value = model;
    if (handle && urlInput && !clean(urlInput.value)) urlInput.value = `${window.location.origin}/products/${encodeURIComponent(handle)}`;
    if (productInput && !clean(productInput.value)) productInput.value = model || handle.replace(/-/g, ' ');
  };

  const analyticsPayload = (form, attribution = {}) => {
    const field = (name) => clean(form?.elements[`contact[${name}]`]?.value);
    return {
      formId: form?.id || '',
      url: window.location.href,
      inquiry_id: field('Inquiry ID'),
      submission_stage: field('Submission stage'),
      request_type: field('Request type'),
      supply_program: field('Supply program'),
      source: field('Source'),
      market: field('Country / market'),
      quantity: field('Quantity'),
      product_handle: field('Product handle'),
      product_model: field('Product / Model'),
      model: field('Model'),
      helmet_type: field('Helmet type'),
      product_url: field('Product URL'),
      ...attribution
    };
  };

  const firedOnce = new Set();
  const fireResult = (name, detail) => {
    const key = `${name}:${detail?.inquiry_id || detail?.formId || ''}:${detail?.submission_stage || ''}`;
    if (firedOnce.has(key)) return;
    firedOnce.add(key);
    dispatch(name, detail);
    if (name === 'form_submit_success') fireLeadEvents(detail);
  };

  let captchaRetried = false;
  let captchaProviderFailed = false;
  const isCaptchaProviderSource = (src = '') => src.includes('storefront-forms-hcaptcha') || src.includes('hcaptcha.com/1/api.js');
  const retryCaptchaProvider = (src) => {
    if (captchaRetried || !src) return false;
    captchaRetried = true;
    try {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.append(script);
      return true;
    } catch (_) { return false; }
  };

  const bindSubmitLifecycle = (root, form, i18n, attribution, life) => {
    if (form.dataset.msSubmitBound === '1') return;
    form.dataset.msSubmitBound = '1';

    const pendingTimeout = Number(root.dataset.msPendingTimeout)
      || Number(window.__MS_INQUIRY_PENDING_TIMEOUT__)
      || PENDING_TIMEOUT_MS;
    const submitButton = q(form, 'button[type="submit"]');
    const statusLine = q(root, '[data-ms-unified-status]');
    const labelNode = submitButton?.querySelector('span');
    const originalLabel = labelNode?.textContent || '';
    let pending = false;
    let pendingStartedAt = 0;
    let tickTimer = null;
    let challengeSeen = false;
    let captchaShownFired = false;
    let startFired = false;
    let viewFired = false;

    const tokenInput = () => form.elements['h-captcha-response'] || form.elements['g-recaptcha-response'] || null;
    const hasToken = () => Boolean(clean(tokenInput()?.value));
    const challengeVisible = () => [...document.querySelectorAll('iframe')].some((frame) => {
      const src = frame.src || '';
      return src.includes('hcaptcha.com') && src.includes('challenge');
    });

    const setStatus = (message, variant = '') => {
      if (!statusLine) return;
      statusLine.textContent = message || '';
      statusLine.className = 'ms-unified-inquiry__status' + (variant ? ` ms-unified-inquiry__status--${variant}` : '');
      statusLine.hidden = !message;
    };

    const setPending = (active) => {
      pending = active;
      if (active) form.dataset.msPending = '1';
      else delete form.dataset.msPending;
      if (submitButton) {
        submitButton.disabled = active;
        submitButton.classList.toggle('is-loading', active);
        if (labelNode && originalLabel) labelNode.textContent = active ? (i18n.sending || 'SENDING…') : originalLabel;
      }
    };

    const stopTimer = () => {
      if (tickTimer) clearInterval(tickTimer);
      tickTimer = null;
    };

    const failPending = (message) => {
      if (!pending) return;
      stopTimer();
      setPending(false);
      setStatus(message || i18n.securityError, 'error');
      const detail = analyticsPayload(form, attribution);
      dispatch('captcha_error', detail);
      try { if (typeof window.gtag === 'function') window.gtag('event', 'captcha_error', detail); } catch (_) {}
    };

    const tick = () => {
      if (!pending) return;
      if (hasToken()) {
        setStatus(i18n.sending || 'SENDING…');
        return;
      }
      if (captchaProviderFailed) {
        failPending(i18n.securityError);
        return;
      }
      const challenge = challengeVisible();
      if (challenge) {
        challengeSeen = true;
        setStatus(i18n.completeSecurity || 'COMPLETE THE SECURITY CHECK TO SEND YOUR INQUIRY', 'security');
        if (!captchaShownFired) {
          captchaShownFired = true;
          dispatch('captcha_shown', analyticsPayload(form, attribution));
        }
        return;
      }
      if (!challengeSeen && !hasToken() && Date.now() - pendingStartedAt > pendingTimeout) failPending(i18n.securityError);
    };

    life.on(form, 'submit', (event) => {
      if (event.defaultPrevented) return;
      if (pending) {
        event.preventDefault();
        return;
      }
      syncGalleryToForm(form);
      pendingStartedAt = Date.now();
      challengeSeen = false;
      captchaShownFired = false;
      setPending(true);
      setStatus(i18n.sending || 'SENDING…');
      const detail = analyticsPayload(form, attribution);
      try { window.sessionStorage.setItem(SUBMIT_SESSION_KEY, JSON.stringify(detail)); } catch (_) {}
      dispatch('form_submit_attempt', detail);
      tick();
      if (!tickTimer) tickTimer = setInterval(tick, CHECK_INTERVAL_MS);
    });

    life.on(form, 'input', () => {
      if (!startFired) {
        startFired = true;
        dispatch('form_start', analyticsPayload(form, attribution));
      }
    });
    life.on(form, 'change', () => {
      if (!startFired) {
        startFired = true;
        dispatch('form_start', analyticsPayload(form, attribution));
      }
    });

    const fireView = () => {
      if (viewFired) return;
      viewFired = true;
      dispatch('form_view', analyticsPayload(form, attribution));
    };
    if ('IntersectionObserver' in window && typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fireView();
            observer.disconnect();
          }
        });
      }, { threshold: 0.2 });
      life.add(() => observer.disconnect());
      observer.observe(form);
    } else {
      fireView();
    }

    life.on(window, 'pagehide', stopTimer);
    life.add(() => {
      stopTimer();
      if (pending) setStatus('');
      setPending(false);
      delete form.dataset.msSubmitBound;
    });
  };

  const handleServerResult = (root, form, attribution) => {
    const result = q(root, '[data-ms-unified-result]');
    if (!result) return;

    const saved = readJson(readStorage('sessionStorage', SUBMIT_SESSION_KEY), {});
    if (saved.formId && saved.formId !== form.id) return;
    const detail = saved.formId ? saved : analyticsPayload(form, attribution);
    const stage = clean(detail.submission_stage || form.elements['contact[Submission stage]']?.value);
    const isError = result.classList.contains('ms-unified-inquiry__feedback--error');

    if (saved.formId) {
      try { window.sessionStorage.removeItem(SUBMIT_SESSION_KEY); } catch (_) {}
    }

    if (stage === BUYER_STAGE) {
      const draft = readDraft();
      if (draft.inquiryId) {
        draft.status = isError ? 'needs-recapture' : 'captured';
        draft.lastSubmission = '';
        draft.updatedAt = new Date().toISOString();
        writeDraft(draft);
      }
      root.dataset.msServerResult = isError ? 'buyer-error' : 'buyer-captured';
      result.hidden = true;
      dispatch(isError ? 'inquiry_buyer_capture_error' : 'inquiry_buyer_info_completed', detail);
      return;
    }

    if (isError) {
      root.dataset.msServerResult = 'error';
      fireResult('form_submit_error', detail);
      return;
    }

    root.dataset.msServerResult = 'success';
    fireResult('form_submit_success', detail);
    if (!result.closest('dialog')) {
      try {
        const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        result.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
        result.focus({ preventScroll: true });
      } catch (_) {}
    }
  };

  const initForm = (root) => {
    if (!root || root.dataset.msUnifiedReady === '1') return;
    const form = q(root, 'form');
    if (!form) return;
    root.dataset.msUnifiedReady = '1';
    const life = createLifecycle();
    formLifecycles.set(root, life);
    life.add(() => {
      delete root.dataset.msUnifiedReady;
      formLifecycles.delete(root);
    });
    const i18n = readI18n(root);
    const attribution = readAttribution();
    applyAttribution(root, attribution);
    applyUrlPrefill(root);
    syncGalleryToForm(form);
    bindSubmitLifecycle(root, form, i18n, attribution, life);
    handleServerResult(root, form, attribution);
  };

  const inferSource = (trigger) => {
    const explicit = clean(trigger?.dataset?.msInquirySource);
    if (explicit) return explicit;
    if (trigger?.closest?.('.ms-floating-actions')) return 'Floating Inquiry';
    if (trigger?.closest?.('.site-header')) return 'Header Inquiry';
    if (trigger?.closest?.('.ms-v12-overview')) return 'Product Page';
    if (trigger?.closest?.('.home-hero')) return 'Homepage Hero';
    if (document.body.classList.contains('template-index')) return 'Homepage Inquiry';
    if (document.body.classList.contains('template-product')) return 'Product Page';
    return 'Website Inquiry';
  };

  const normalizeProgram = (value) => clean(value).toLowerCase().includes('oem') ? 'oem_private_label' : 'standard_wholesale';

  const initInquiryDialog = (dialog) => {
    if (!dialog || dialog.dataset.msInquiryReady === '1') return;
    const root = q(dialog, '[data-ms-unified-inquiry]');
    const form = q(root, 'form');
    if (!root || !form) return;
    dialog.dataset.msInquiryReady = '1';
    const life = createLifecycle();
    dialogLifecycles.set(dialog, life);
    life.add(() => {
      if (dialog.open) dialog.close();
      if (!q(document, '[data-ms-inquiry-dialog][open]')) document.body.classList.remove('ms-inquiry-open');
      delete dialog.dataset.msInquiryReady;
      dialogLifecycles.delete(dialog);
    });

    if (dialog.hasAttribute('data-ms-single-form')) {
      let trigger;
      const close = () => {
        dialog.close();
        document.body.classList.remove('ms-inquiry-open');
        if (trigger?.isConnected) trigger.focus();
      };
      const open = (button) => {
        trigger = button || document.activeElement;
        const source = q(form, '[data-ms-unified-source]');
        if (source) source.value = inferSource(button);
        syncGalleryToForm(form);
        if (!dialog.open) dialog.showModal();
        document.body.classList.add('ms-inquiry-open');
        (q(root, '[data-ms-unified-result]') || q(form, 'input:not([type="hidden"])'))?.focus();
      };
      life.on(q(dialog, '[data-ms-inquiry-close]'), 'click', close);
      life.on(dialog, 'cancel', (event) => { event.preventDefault(); close(); });
      life.on(dialog, 'click', (event) => { if (event.target === dialog) close(); });
      life.on(document, 'click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const button = event.target.closest('[data-ms-inquiry-open],a[href*="' + INQUIRY_LINK_FRAGMENT + '"]');
        if (!button || dialog.contains(button)) return;
        event.preventDefault(); open(button);
      });
      life.on(window, 'ms:inquiry:open', (event) => open(event.detail?.trigger));
      if (root.dataset.msServerResult) open(null);
      return;
    }


    const attribution = readAttribution();
    const closeButton = q(dialog, '[data-ms-inquiry-close]');
    const steps = qa(dialog, '[data-ms-inquiry-step]');
    const progress = qa(dialog, '[data-ms-inquiry-progress]');
    const typeButtons = qa(dialog, '[data-ms-inquiry-type-choice]');
    const helmetButtons = qa(dialog, '[data-ms-helmet-choice]');
    const currentProductChoice = q(dialog, '[data-ms-current-product-choice]');
    const configRoot = q(dialog, '[data-ms-inquiry-configurator]');
    const configPanels = qa(dialog, '[data-ms-config-panel]');
    const configFields = qa(dialog, '[data-ms-config-field]');
    const requestTypeInput = q(form, '[data-ms-inquiry-request-type]');
    const programInput = q(form, '[data-ms-inquiry-program]');
    const inquiryIdInput = q(form, '[data-ms-inquiry-id]');
    const submissionStageInput = q(form, '[data-ms-inquiry-stage]');
    const sourceInput = q(form, '[data-ms-unified-source]');
    const handleInput = q(form, '[data-ms-unified-handle]');
    const modelInput = q(form, '[data-ms-unified-model]');
    const urlInput = q(form, '[data-ms-unified-url]');
    const typeInput = q(form, '[data-ms-hidden="type"]');
    const productInput = q(form, '[data-ms-unified-product]');
    const programSummary = q(dialog, '[data-ms-inquiry-program-summary]');
    const helmetSummary = q(dialog, '[data-ms-inquiry-helmet-summary]');
    const summaryImage = q(dialog, '[data-ms-inquiry-summary-image]');
    const wizardNotes = q(dialog, '[data-ms-wizard-notes]');
    const configError = q(dialog, '[data-ms-wizard-config-error]');
    const buyerNextButton = q(dialog, '[data-ms-wizard-buyer-next]');
    const buyerFields = {
      name: q(dialog, '[data-ms-wizard-name]'),
      market: q(dialog, '[data-ms-wizard-market]'),
      quantity: q(dialog, '[data-ms-wizard-quantity]'),
      email: q(dialog, '[data-ms-wizard-email]'),
      phone: q(dialog, '[data-ms-wizard-phone]')
    };
    const formFields = {
      name: q(form, '[data-ms-unified-name]'),
      market: q(form, '[data-ms-unified-market]'),
      quantity: q(form, '[data-ms-unified-quantity]'),
      email: q(form, '[data-ms-unified-email]'),
      phone: q(form, '[data-ms-unified-phone]'),
      notes: q(form, '[data-ms-unified-notes]')
    };
    let lastTrigger = null;
    let activeStep = 1;

    const selectedGalleryConfiguration = () => {
      const path = productPath(urlInput?.value);
      return path ? galleryConfigurations.get(path) || null : null;
    };

    const fieldByName = (name) => form.elements[`contact[${name}]`] || null;
    const setValue = (field, value, emit = false) => {
      if (!field) return;
      const next = String(value || '');
      if (field.value === next) return;
      field.value = next;
      if (emit) field.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const captureBuyer = () => ({
      name: clean(buyerFields.name?.value || formFields.name?.value),
      market: clean(buyerFields.market?.value || formFields.market?.value),
      quantity: clean(buyerFields.quantity?.value || formFields.quantity?.value),
      email: clean(buyerFields.email?.value || formFields.email?.value),
      phone: clean(buyerFields.phone?.value || formFields.phone?.value)
    });

    const selectedHelmetButton = () => helmetButtons.find((button) => button.classList.contains('is-selected')) || null;
    const captureProduct = () => {
      const selected = selectedHelmetButton();
      return {
        handle: clean(handleInput?.value),
        model: clean(modelInput?.value),
        url: clean(urlInput?.value),
        product: clean(productInput?.value),
        type: clean(typeInput?.value),
        title: clean(selected?.dataset.helmetTitle || productInput?.value),
        image: clean(selected?.dataset.helmetImage)
      };
    };

    const currentIntent = () => clean(requestTypeInput?.value || programInput?.value).toLowerCase().includes('oem') ? 'oem' : 'standard';
    const hasConfiguredIntent = () => clean(fieldByName('Version')?.value);
    const configuredIntent = () => clean(fieldByName('Version')?.value).toLowerCase().includes('oem') ? 'oem' : 'standard';

    const sameJourney = (draft) => {
      if (!draft?.inquiryId) return false;
      const currentHandle = clean(handleInput?.value || currentProductChoice?.dataset.helmetHandle || dialog.dataset.currentProductHandle);
      const draftHandle = clean(draft.product?.handle);
      return !currentHandle || !draftHandle || currentHandle === draftHandle;
    };

    const updateDraft = (changes = {}) => {
      const draft = readDraft();
      if (!draft.inquiryId) return draft;
      const next = Object.assign({}, draft, changes, { updatedAt: new Date().toISOString() });
      writeDraft(next);
      return next;
    };

    const configurationDraftKey = (handle, intent) => `${clean(handle) || 'generic'}::${intent === 'oem' ? 'oem' : 'standard'}`;
    const readConfigurationDraft = (handle, intent) => readDraft().configurationsByProduct?.[configurationDraftKey(handle, intent)] || null;
    const restoreConfigurationControls = (saved) => {
      const fields = saved?.fields;
      if (!fields || typeof fields !== 'object') return;
      configFields.forEach((control) => {
        const name = clean(control.dataset.contactName);
        if (Object.prototype.hasOwnProperty.call(fields, name)) control.value = String(fields[name] ?? '');
      });
    };
    const saveConfigurationDraft = () => {
      const draft = readDraft();
      if (!draft.inquiryId) return;
      if (activeStep !== 3) return;
      if (configRoot?.dataset.capabilityState === 'loading') return;
      const intent = currentIntent();
      const handle = clean(handleInput?.value);
      const activePanel = q(configRoot, `[data-ms-config-panel="${intent}"]`);
      const fields = {};
      qa(activePanel, '[data-ms-config-field]').forEach((control) => {
        const name = clean(control.dataset.contactName);
        if (name && !control.disabled && !control.closest('[hidden]')) fields[name] = control.value;
      });
      const key = configurationDraftKey(handle, intent);
      updateDraft({ configurationsByProduct: Object.assign({}, draft.configurationsByProduct, { [key]: { intent, fields } }) });
    };

    const clearConfigurationFields = () => {
      [
        'Version', 'Helmet color', 'Wholesale MOQ', 'Packaging', 'Customization type',
        'Custom color / Pantone', 'Surface finish', 'Graphic customization type',
        'Graphic description', 'Visor color', 'Visor material', 'Logo', 'Logo position',
        'Logo file', 'Helmet dust bag', 'Standard 3-layer carton', 'Inner liner',
        'Target market', 'Configuration summary', 'OEM configuration JSON'
      ].forEach((name) => setValue(fieldByName(name), ''));
    };

    const resetConfigControls = () => {
      configFields.forEach((control) => {
        if (control.tagName === 'SELECT') {
          const preferred = clean(control.dataset.default);
          if (preferred && [...control.options].some((option) => option.value === preferred)) control.value = preferred;
          else {
            const empty = [...control.options].find((option) => option.value === '');
            if (empty) control.value = '';
            else control.selectedIndex = 0;
          }
        } else {
          control.value = '';
        }
      });
    };

    const syncConfigurationToForm = () => {
      const intent = currentIntent();
      clearConfigurationFields();
      setValue(fieldByName('Version'), intent === 'oem' ? 'OEM / Private Label' : 'MASONTEX Standard Wholesale');
      setValue(fieldByName('Customization type'), intent === 'oem' ? 'OEM / Private Label' : 'Standard MASONTEX Supply');
      setValue(fieldByName('Target market'), buyerFields.market?.value || '');
      setValue(fieldByName('Wholesale MOQ'), 'MOQ from 100 PCS; flexible for trial orders');

      const activePanel = q(configRoot, `[data-ms-config-panel="${intent}"]`);
      const details = {};
      const summary = [];
      const gallery = selectedGalleryConfiguration();
      qa(activePanel, '[data-ms-config-field]').forEach((control) => {
        if (control.disabled || control.closest('[hidden]')) return;
        const value = clean(control.value);
        if (!value) return;
        const name = clean(control.dataset.contactName);
        const label = clean(control.dataset.summaryLabel);
        if (name) {
          setValue(fieldByName(name), value);
          details[name] = value;
        }
        if (label) summary.push(`${label}: ${value}`);
      });

      const programLabel = intent === 'oem' ? 'OEM / Private Label' : 'MASONTEX Standard Wholesale';
      const gallerySummary = gallery && [
        ['Model', gallery.model],
        ['Color', gallery.color],
        ['Visor', gallery.visor],
        ['Logo', gallery.logo],
        ['Structure', gallery.structure],
        ['View', gallery.view],
        ['Custom options', gallery.customOptions],
        ['Gallery notes', gallery.notes]
      ].map(([label, value]) => {
        const text = Array.isArray(value)
          ? value.map(clean).filter(Boolean).join(', ')
          : value && typeof value === 'object'
            ? JSON.stringify(value)
            : clean(value);
        return text ? `${label}: ${text}` : '';
      }).filter(Boolean).join(', ');
      setValue(fieldByName('Configuration summary'), [programLabel, ...summary, gallerySummary && `Product gallery: ${gallerySummary}`].filter(Boolean).join(' · '));
      const payload = {
        program: intent,
        market: clean(buyerFields.market?.value),
        quantity: clean(buyerFields.quantity?.value) ? `${clean(buyerFields.quantity.value)} PCS` : '',
        configuration: details
      };
      if (gallery) payload.productGallery = gallery;
      setValue(fieldByName('OEM configuration JSON'), JSON.stringify(payload));
    };

    const setConfigIntent = (intent, reset = false) => {
      const resolved = intent === 'oem' ? 'oem' : 'standard';
      if (reset) resetConfigControls();
      configRoot.dataset.intent = resolved;
      configPanels.forEach((panel) => { panel.hidden = panel.dataset.msConfigPanel !== resolved; });
      const heading = q(configRoot, '[data-ms-config-heading]');
      const copy = q(configRoot, '[data-ms-config-copy]');
      if (resolved === 'oem') {
        if (heading) heading.textContent = 'Define the key OEM / private-label requirements.';
        if (copy) copy.textContent = 'Add the requirements you already know. Leave undecided items blank and we will confirm them during quotation.';
      } else {
        if (heading) heading.textContent = 'MASONTEX standard wholesale specification.';
        if (copy) copy.textContent = 'MASONTEX branding, export bulk packing and QC standards are fixed. Add a preferred standard color if needed.';
      }
      syncConfigurationToForm();
    };

    const hydrateConfigurationFromForm = () => {
      const handle = clean(handleInput?.value);
      const saved = readConfigurationDraft(handle, currentIntent());
      const version = clean(saved?.intent || fieldByName('Version')?.value).toLowerCase();
      if (!version) {
        restoreConfigurationControls(saved);
        setConfigIntent(currentIntent(), false);
        return;
      }
      const intent = version.includes('oem') ? 'oem' : 'standard';
      configRoot.dataset.intent = intent;
      configPanels.forEach((panel) => { panel.hidden = panel.dataset.msConfigPanel !== intent; });
      if (saved) restoreConfigurationControls(saved);
      else {
        const activePanel = q(configRoot, `[data-ms-config-panel="${intent}"]`);
        qa(activePanel, '[data-ms-config-field]').forEach((control) => {
          const stored = clean(fieldByName(control.dataset.contactName)?.value);
          if (stored) control.value = stored;
        });
      }
      setConfigIntent(intent, false);
    };

    const syncBuyerFromForm = () => {
      Object.keys(buyerFields).forEach((key) => {
        if (buyerFields[key] && formFields[key]) buyerFields[key].value = formFields[key].value || '';
      });
      if (wizardNotes && formFields.notes) wizardNotes.value = formFields.notes.value || '';
    };

    const syncBuyerToForm = (validate = false) => {
      const ordered = ['name', 'market', 'quantity', 'email'];
      if (validate) {
        for (const key of ordered) {
          const field = buyerFields[key];
          if (field && !field.checkValidity()) {
            field.reportValidity();
            field.focus();
            return false;
          }
        }
      }
      Object.keys(buyerFields).forEach((key) => {
        if (buyerFields[key] && formFields[key]) setValue(formFields[key], buyerFields[key].value, true);
      });
      return true;
    };

    const syncNotesToForm = () => {
      if (wizardNotes && formFields.notes) setValue(formFields.notes, wizardNotes.value, true);
    };

    const reviewText = (selector, value, fallback = '—') => {
      const node = q(dialog, selector);
      if (node) node.textContent = clean(value) || fallback;
    };

    const updateReview = () => {
      syncBuyerToForm(false);
      syncNotesToForm();
      syncConfigurationToForm();
      const product = captureProduct();
      const configuration = clean(fieldByName('Configuration summary')?.value);
      const configurationWrap = q(dialog, '[data-ms-review-configuration-wrap]');
      const reviewImage = q(dialog, '[data-ms-review-image]');
      const reviewPlaceholder = q(dialog, '[data-ms-review-image-placeholder]');

      reviewText('[data-ms-review-program]', programInput?.value || requestTypeInput?.value);
      reviewText('[data-ms-review-model]', product.model || product.type || product.title);
      reviewText('[data-ms-review-product]', product.title || product.product || product.type);
      reviewText('[data-ms-review-buyer]', formFields.name?.value);
      reviewText('[data-ms-review-market]', formFields.market?.value);
      reviewText('[data-ms-review-quantity]', formFields.quantity?.value ? `${clean(formFields.quantity.value)} PCS` : '');
      reviewText('[data-ms-review-email]', formFields.email?.value);
      reviewText('[data-ms-review-phone]', formFields.phone?.value, 'Not provided');
      reviewText('[data-ms-review-configuration]', configuration);
      reviewText('[data-ms-review-notes]', formFields.notes?.value, 'No additional requirements');
      if (configurationWrap) configurationWrap.hidden = !configuration;

      if (reviewImage) {
        if (product.image) {
          reviewImage.src = product.image;
          reviewImage.alt = [product.model, product.title].filter(Boolean).join(' · ');
          reviewImage.hidden = false;
          if (reviewPlaceholder) reviewPlaceholder.hidden = true;
        } else {
          reviewImage.hidden = true;
          reviewImage.removeAttribute('src');
          if (reviewPlaceholder) reviewPlaceholder.hidden = false;
        }
      }
    };

    const reviewEventPayload = () => ({
      inquiry_id: clean(inquiryIdInput?.value || readDraft().inquiryId),
      program: normalizeProgram(requestTypeInput?.value || programInput?.value),
      source: clean(sourceInput?.value),
      market: clean(formFields.market?.value),
      quantity: clean(formFields.quantity?.value),
      product_handle: clean(handleInput?.value),
      model: clean(modelInput?.value),
      helmet_type: clean(typeInput?.value),
      ...attribution
    });

    const setStep = (number, focus = true) => {
      const previousStep = activeStep;
      activeStep = Math.max(1, Math.min(Number(number) || 1, 4));
      steps.forEach((step) => { step.hidden = Number(step.dataset.msInquiryStep) !== activeStep; });
      progress.forEach((item) => {
        const index = Number(item.dataset.msInquiryProgress);
        item.classList.toggle('is-active', index === activeStep);
        item.classList.toggle('is-complete', index < activeStep);
      });
      dialog.dataset.msInquiryStep = String(activeStep);
      if (activeStep === 4) {
        updateReview();
        if (previousStep !== 4) dispatch('inquiry_review_view', reviewEventPayload());
      }
      if (focus) {
        life.later(() => {
          const current = steps.find((step) => Number(step.dataset.msInquiryStep) === activeStep);
          const target = q(current, 'button:not([hidden]),input:not([type="hidden"]),textarea,select,h3');
          target?.focus?.({ preventScroll: true });
        }, 40);
      }
    };

    const ensureBuyerStatus = () => {
      let status = q(dialog, '[data-ms-early-capture-status]');
      if (status) return status;
      const actions = buyerNextButton?.closest('.ms-unified-inquiry__actions');
      if (!actions) return null;
      status = document.createElement('p');
      status.className = 'ms-unified-inquiry__status';
      status.dataset.msEarlyCaptureStatus = '';
      status.hidden = true;
      actions.insertAdjacentElement('afterend', status);
      return status;
    };

    const setBuyerStatus = (message, variant = '') => {
      const status = ensureBuyerStatus();
      if (!status) return;
      status.textContent = message || '';
      status.className = 'ms-unified-inquiry__status' + (variant ? ` ms-unified-inquiry__status--${variant}` : '');
      status.hidden = !message;
    };

    const setBuyerPending = (pending) => {
      if (!buyerNextButton) return;
      const label = q(buyerNextButton, 'span');
      buyerNextButton.disabled = pending;
      buyerNextButton.classList.toggle('is-loading', pending);
      if (label) label.textContent = pending ? 'SENDING…' : 'SEND INQUIRY & CONTINUE';
    };

    const setProgram = (button, advance = true, userAction = false) => {
      if (!button) return;
      const requestType = clean(button.dataset.requestType) || 'Wholesale / OEM Request';
      const label = clean(button.dataset.programLabel) || requestType;
      const draft = readDraft();
      if (userAction) saveConfigurationDraft();
      const previousRequestType = clean(draft.requestType);
      const nextIntent = requestType.toLowerCase().includes('oem') ? 'oem' : 'standard';
      const previousIntent = hasConfiguredIntent() ? configuredIntent() : currentIntent();

      typeButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      setValue(requestTypeInput, requestType, true);
      setValue(programInput, label, true);
      if (programSummary) programSummary.textContent = label;

      if (previousIntent && previousIntent !== nextIntent) {
        clearConfigurationFields();
        resetConfigControls();
        restoreConfigurationControls(readConfigurationDraft(clean(handleInput?.value), nextIntent));
        setConfigIntent(nextIntent, false);
      } else {
        setConfigIntent(nextIntent, !hasConfiguredIntent());
      }

      if (draft.inquiryId) {
        const status = draft.status === 'captured' && previousRequestType && previousRequestType !== requestType
          ? 'needs-recapture'
          : draft.status;
        updateDraft({ requestType, program: label, status });
      }

      if (userAction) {
        dispatch('inquiry_order_type_selected', {
          inquiry_id: clean(inquiryIdInput?.value || draft.inquiryId),
          program: normalizeProgram(requestType),
          source: clean(sourceInput?.value),
          ...attribution
        });
      }
      if (advance) setStep(2);
    };

    const clearHelmetSelection = () => {
      helmetButtons.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      setValue(handleInput, '');
      setValue(modelInput, '');
      setValue(urlInput, '');
      setValue(typeInput, '');
      setValue(productInput, '');
      clearConfigurationFields();
      if (helmetSummary) helmetSummary.textContent = '—';
      if (summaryImage) {
        summaryImage.hidden = true;
        summaryImage.removeAttribute('src');
      }
    };

    const selectHelmet = (button, preserveConfiguration = false, userAction = false) => {
      if (!button) return;
      helmetButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (!preserveConfiguration) {
        if (userAction) saveConfigurationDraft();
        clearConfigurationFields();
        resetConfigControls();
      }
      const kind = clean(button.dataset.helmetKind);
      const title = clean(button.dataset.helmetTitle);
      const model = clean(button.dataset.helmetModel);
      const handle = clean(button.dataset.helmetHandle);
      const relativeUrl = clean(button.dataset.helmetUrl);
      const helmetType = clean(button.dataset.helmetType) || title;
      const image = clean(button.dataset.helmetImage);

      setValue(handleInput, kind === 'category' ? '' : handle, userAction);
      setValue(modelInput, kind === 'category' ? '' : model, userAction);
      if (kind === 'category') setValue(urlInput, '', userAction);
      else if (relativeUrl) {
        try { setValue(urlInput, new URL(relativeUrl, window.location.origin).toString(), userAction); }
        catch (_) { setValue(urlInput, relativeUrl, userAction); }
      } else setValue(urlInput, '', userAction);
      setValue(typeInput, helmetType, userAction);
      setValue(productInput, title || model || helmetType, userAction);

      if (helmetSummary) {
        const summary = model && title && model !== title ? `${model} · ${title}` : (model || title || helmetType);
        helmetSummary.textContent = summary || '—';
      }
      if (summaryImage) {
        if (image) {
          summaryImage.src = image;
          summaryImage.alt = [model, title].filter(Boolean).join(' · ');
          summaryImage.hidden = false;
        } else {
          summaryImage.hidden = true;
          summaryImage.removeAttribute('src');
        }
      }
      if (configError) configError.hidden = true;
      if (!preserveConfiguration) restoreConfigurationControls(readConfigurationDraft(handle, currentIntent()));
      setConfigIntent(currentIntent(), false);

      const draft = readDraft();
      if (userAction && draft.inquiryId) updateDraft({ product: captureProduct() });
      if (userAction) {
        dispatch('inquiry_helmet_selected', {
          inquiry_id: clean(inquiryIdInput?.value || draft.inquiryId),
          program: normalizeProgram(requestTypeInput?.value || programInput?.value),
          product_handle: kind === 'category' ? '' : handle,
          model,
          helmet_type: helmetType,
          ...attribution
        });
      }
    };

    const setRawProductContext = (handle, model, preserveConfiguration = false, userAction = false) => {
      helmetButtons.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      if (!preserveConfiguration) {
        if (userAction) saveConfigurationDraft();
        clearConfigurationFields();
        resetConfigControls();
      }
      setValue(handleInput, handle, userAction);
      setValue(modelInput, model, userAction);
      setValue(typeInput, '', userAction);
      setValue(productInput, model || handle.replace(/-/g, ' '), userAction);
      if (handle) setValue(urlInput, `${window.location.origin}/products/${encodeURIComponent(handle)}`, userAction);
      else setValue(urlInput, '', userAction);
      if (helmetSummary) helmetSummary.textContent = model || handle.replace(/-/g, ' ') || '—';
      if (summaryImage) {
        summaryImage.hidden = true;
        summaryImage.removeAttribute('src');
      }
      if (!preserveConfiguration) restoreConfigurationControls(readConfigurationDraft(handle, currentIntent()));
      setConfigIntent(currentIntent(), false);
      const draft = readDraft();
      if (userAction && draft.inquiryId) updateDraft({ product: captureProduct() });
    };

    const resetProgram = () => {
      typeButtons.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      setValue(requestTypeInput, '');
      setValue(programInput, '');
      if (programSummary) programSummary.textContent = '—';
    };

    const applyTriggerContext = (trigger, preserveConfiguration = false) => {
      const source = inferSource(trigger);
      setValue(sourceInput, source);
      root.dataset.msSource = source;

      let handle = '';
      let model = '';
      const href = trigger?.getAttribute?.('href') || '';
      if (href) {
        try {
          const url = new URL(href, window.location.href);
          handle = clean(url.searchParams.get('product'));
          model = clean(url.searchParams.get('model'));
        } catch (_) {}
      }

      const currentHandle = clean(currentProductChoice?.dataset.helmetHandle || dialog.dataset.currentProductHandle);
      if (currentProductChoice && (!handle || handle === currentHandle)) selectHelmet(currentProductChoice, preserveConfiguration, false);
      else if (handle || model) setRawProductContext(handle, model, preserveConfiguration, false);
      else if (currentProductChoice) selectHelmet(currentProductChoice, preserveConfiguration, false);
      else clearHelmetSelection();
    };

    const restoreDraft = (draft) => {
      if (!draft?.inquiryId) return;
      setValue(inquiryIdInput, draft.inquiryId);
      setValue(submissionStageInput, draft.status === 'captured' ? BUYER_STAGE : '');
      const requestType = clean(draft.requestType);
      const program = clean(draft.program);
      setValue(requestTypeInput, requestType);
      setValue(programInput, program);
      typeButtons.forEach((button) => {
        const selected = clean(button.dataset.requestType) === requestType || clean(button.dataset.programLabel) === program;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (programSummary) programSummary.textContent = program || requestType || '—';

      const buyer = draft.buyer || {};
      Object.keys(buyerFields).forEach((key) => {
        if (buyerFields[key] && buyer[key] != null) buyerFields[key].value = buyer[key];
      });
      syncBuyerToForm(false);

      const product = draft.product || {};
      const matched = helmetButtons.find((button) => {
        const handleMatch = product.handle && clean(button.dataset.helmetHandle) === clean(product.handle);
        const modelMatch = product.model && clean(button.dataset.helmetModel) === clean(product.model);
        return handleMatch || modelMatch;
      });
      if (matched) selectHelmet(matched, true, false);
      else if (product.handle || product.model) setRawProductContext(product.handle || '', product.model || '', true, false);
      hydrateConfigurationFromForm();
    };

    const activateSuccessState = () => {
      const success = q(dialog, '[data-ms-inquiry-success]');
      if (!success) return;
      success.hidden = false;
      dialog.classList.add('is-success');
      life.later(() => success.focus?.({ preventScroll: true }), 80);
    };

    const resetSuccessState = (clearJourney = false) => {
      dialog.classList.remove('is-success');
      const success = q(dialog, '[data-ms-inquiry-success]');
      if (success) success.hidden = true;
      if (clearJourney) clearDraft();
      delete root.dataset.msServerResult;
      setValue(inquiryIdInput, '');
      setValue(submissionStageInput, '');
    };

    const showDialog = () => {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else dialog.setAttribute('open', '');
      document.body.classList.add('ms-inquiry-open');
    };

    const closeDialog = () => {
      const completed = dialog.classList.contains('is-success');
      if (completed) resetSuccessState(true);
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      document.body.classList.remove('ms-inquiry-open');
      life.later(() => lastTrigger?.focus?.(), 20);
    };

    const openDialog = (trigger, options = {}) => {
      lastTrigger = trigger || document.activeElement;
      const serverResult = clean(root.dataset.msServerResult);
      const preserveConfiguration = Boolean(options.preserveConfiguration);

      if (serverResult) {
        const draft = readDraft();
        if (draft.inquiryId) restoreDraft(draft);
        syncBuyerFromForm();
        if (serverResult === 'buyer-captured') {
          setBuyerPending(false);
          setBuyerStatus('');
          setStep(3, false);
        } else if (serverResult === 'buyer-error') {
          setBuyerPending(false);
          setBuyerStatus('We could not send your inquiry. Please check the information and try again.', 'error');
          setStep(2, false);
        } else {
          setStep(4, false);
          if (serverResult === 'success') activateSuccessState();
        }
        showDialog();
        return;
      }

      resetProgram();
      applyTriggerContext(trigger, preserveConfiguration);
      const draft = readDraft();
      if (draft.inquiryId && sameJourney(draft) && draft.status === 'captured') {
        restoreDraft(draft);
        setBuyerPending(false);
        setBuyerStatus('');
        setStep(3, false);
      } else {
        if (draft.inquiryId && !sameJourney(draft)) clearDraft();
        const requestedIntent = clean(options.intent || trigger?.dataset?.msInquiryIntent).toLowerCase();
        if (requestedIntent === 'oem') {
          const button = typeButtons.find((item) => clean(item.dataset.requestType).toLowerCase().includes('oem'));
          if (button) setProgram(button, false, false);
        } else if (requestedIntent === 'standard' || requestedIntent === 'wholesale') {
          const button = typeButtons.find((item) => clean(item.dataset.requestType).toLowerCase().includes('standard'));
          if (button) setProgram(button, false, false);
        }
        syncBuyerFromForm();
        setStep(1, false);
      }

      showDialog();
      dispatch('inquiry_open', {
        source: clean(sourceInput?.value),
        product_handle: clean(handleInput?.value),
        model: clean(modelInput?.value),
        ...attribution
      });
      life.later(() => {
        const current = steps.find((step) => Number(step.dataset.msInquiryStep) === activeStep);
        (q(current, 'button:not([hidden]),input:not([type="hidden"]),textarea,select,h3') || closeButton)?.focus?.({ preventScroll: true });
      }, 50);
    };

    const submitBuyerCapture = () => {
      if (!syncBuyerToForm(true)) return;
      const requestType = clean(requestTypeInput?.value);
      const program = clean(programInput?.value);
      if (!requestType && !program) {
        setBuyerStatus('Please choose MASONTEX Standard Wholesale or OEM / Private Label first.', 'error');
        setStep(1);
        return;
      }

      const previous = readDraft();
      const inquiryId = previous.inquiryId && sameJourney(previous) ? previous.inquiryId : makeInquiryId();
      setValue(inquiryIdInput, inquiryId);
      setValue(submissionStageInput, BUYER_STAGE);
      writeDraft({
        ...previous,
        inquiryId,
        status: 'pending',
        lastSubmission: 'buyer',
        requestType,
        program,
        buyer: captureBuyer(),
        product: captureProduct(),
        sourceUrl: window.location.href,
        updatedAt: new Date().toISOString()
      });
      setBuyerPending(true);
      setBuyerStatus('SENDING…');
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    };

    const nativeStatus = q(root, '[data-ms-unified-status]');
    if (nativeStatus) {
      const observer = new MutationObserver(() => {
        const draft = readDraft();
        if (draft.lastSubmission !== 'buyer' || draft.status !== 'pending') return;
        const message = clean(nativeStatus.textContent);
        if (!message) return;
        const variant = nativeStatus.classList.contains('ms-unified-inquiry__status--error') ? 'error'
          : nativeStatus.classList.contains('ms-unified-inquiry__status--security') ? 'security'
          : '';
        setBuyerStatus(message, variant);
        if (variant === 'error') setBuyerPending(false);
      });
      observer.observe(nativeStatus, { childList: true, characterData: true, subtree: true, attributes: true });
      life.add(() => observer.disconnect());
    }

    Object.keys(buyerFields).forEach((key) => {
      const visible = buyerFields[key];
      const actual = formFields[key];
      if (!visible || !actual) return;
      const sync = () => {
        setValue(actual, visible.value, true);
        const draft = readDraft();
        if (draft.inquiryId && draft.status !== 'captured') updateDraft({ buyer: captureBuyer() });
        syncConfigurationToForm();
      };
      life.on(visible, 'input', sync);
      life.on(visible, 'change', sync);
    });

    life.on(wizardNotes, 'input', syncNotesToForm);
    life.on(wizardNotes, 'change', syncNotesToForm);
    const syncAndSaveConfiguration = () => {
      syncConfigurationToForm();
      saveConfigurationDraft();
    };
    life.on(configRoot, 'input', syncAndSaveConfiguration);
    life.on(configRoot, 'change', syncAndSaveConfiguration);

    life.on(buyerNextButton, 'click', (event) => {
      event.preventDefault();
      if (!syncBuyerToForm(true)) return;
      const draft = readDraft();
      const unchanged = draft.status === 'captured'
        && draft.inquiryId
        && sameJourney(draft)
        && sameObject(captureBuyer(), draft.buyer)
        && clean(draft.requestType) === clean(requestTypeInput?.value);
      if (unchanged) {
        restoreDraft(draft);
        setStep(3);
        return;
      }
      submitBuyerCapture();
    });

    typeButtons.forEach((button) => life.on(button, 'click', () => setProgram(button, true, true)));
    helmetButtons.forEach((button) => life.on(button, 'click', () => {
      const preserveConfiguration = button === selectedHelmetButton();
      selectHelmet(button, preserveConfiguration, true);
    }));

    life.on(q(dialog, '[data-ms-wizard-review-next]'), 'click', () => {
      syncBuyerToForm(false);
      syncNotesToForm();
      syncConfigurationToForm();
      if (!clean(productInput?.value) && !clean(typeInput?.value)) {
        if (configError) configError.hidden = false;
        helmetButtons[0]?.focus?.();
        return;
      }
      const payload = reviewEventPayload();
      dispatch('inquiry_configuration_completed', {
        ...payload,
        has_custom_color: Boolean(clean(fieldByName('Custom color / Pantone')?.value)),
        has_logo: Boolean(clean(fieldByName('Logo')?.value)),
        has_graphics: Boolean(clean(fieldByName('Graphic customization type')?.value)),
        has_custom_packaging: /private|custom|alternative/i.test(clean(fieldByName('Packaging')?.value))
      });
      setStep(4);
    });

    qa(dialog, '[data-ms-inquiry-back]').forEach((button) => {
      life.on(button, 'click', () => setStep(Number(button.dataset.msInquiryBack) || 1));
    });

    qa(dialog, '[data-ms-review-edit]').forEach((button) => {
      life.on(button, 'click', () => {
        const section = clean(button.dataset.msReviewEdit);
        const target = section === 'order_type' ? 1 : section === 'buyer_information' ? 2 : 3;
        dispatch('inquiry_review_edit', { ...reviewEventPayload(), section });
        setStep(target);
      });
    });

    life.on(q(dialog, '[data-ms-inquiry-success-close]'), 'click', closeDialog);
    life.on(closeButton, 'click', closeDialog);
    life.on(dialog, 'cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    life.on(dialog, 'close', () => document.body.classList.remove('ms-inquiry-open'));
    life.on(dialog, 'click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    life.on(form, 'submit', () => {
      const draft = readDraft();
      const stage = clean(submissionStageInput?.value);
      if (stage === BUYER_STAGE && draft.lastSubmission === 'buyer') return;
      const inquiryId = draft.inquiryId || clean(inquiryIdInput?.value) || makeInquiryId();
      setValue(inquiryIdInput, inquiryId);
      setValue(submissionStageInput, CONFIG_STAGE);
      writeDraft({
        ...draft,
        inquiryId,
        status: 'captured',
        lastSubmission: 'configuration',
        requestType: clean(requestTypeInput?.value),
        program: clean(programInput?.value),
        buyer: captureBuyer(),
        product: captureProduct(),
        updatedAt: new Date().toISOString()
      });
    }, true);

    life.on(document, 'click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest(`[data-ms-inquiry-open],a[href*="${INQUIRY_LINK_FRAGMENT}"]`);
      if (!trigger || dialog.contains(trigger)) return;
      event.preventDefault();
      openDialog(trigger, { preserveConfiguration: false });
    });

    life.on(window, 'ms:inquiry:open', (event) => openDialog(event.detail?.trigger || null, event.detail || {}));

    setBuyerPending(false);
    const nativeGrid = q(root, '.ms-unified-inquiry__grid');
    if (nativeGrid) nativeGrid.hidden = true;
    resetConfigControls();
    if (currentProductChoice) selectHelmet(currentProductChoice, true, false);
    else clearHelmetSelection();
    resetProgram();
    syncBuyerFromForm();
    setStep(1, false);

    if (root.dataset.msServerResult) openDialog(null);
  };

  const onResourceError = (event) => {
    const src = event.target?.src || '';
    if (!isCaptchaProviderSource(src)) return;
    if (retryCaptchaProvider(src)) return;
    captchaProviderFailed = true;
    document.querySelectorAll('[data-ms-unified-inquiry]').forEach((root) => {
      const form = q(root, 'form');
      if (!form || form.dataset.msPending !== '1') return;
      const statusLine = q(root, '[data-ms-unified-status]');
      if (!statusLine) return;
      const i18n = readI18n(root);
      statusLine.textContent = i18n.securityError || 'The security check could not be completed. Please refresh the page and try again.';
      statusLine.className = 'ms-unified-inquiry__status ms-unified-inquiry__status--error';
      statusLine.hidden = false;
    });
  };
  document.addEventListener('error', onResourceError, true);

  const within = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...qa(scope, selector)];
  const boot = (scope = document) => {
    within(scope, '[data-ms-unified-inquiry]').forEach(initForm);
    within(scope, '[data-ms-inquiry-dialog]').forEach(initInquiryDialog);
  };
  const unload = (scope) => {
    within(scope, '[data-ms-inquiry-dialog]').forEach((dialog) => dialogLifecycles.get(dialog)?.destroy());
    within(scope, '[data-ms-unified-inquiry]').forEach((root) => formLifecycles.get(root)?.destroy());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
